import React from "react";

export interface MilestoneProps {
  title: string;
  detail: string;
  /** A stop that has already happened is filled; one still to come is hollow. */
  done?: boolean;
  /** The last stop, so no line continues past its dot. */
  last?: boolean;
  /**
   * A pill beside the title — what this stop *gets* you, as against what
   * happens on it. Only the trial's first stop carries one so far: the moment
   * the button is pressed the product is open, and that is worth saying louder
   * than the date it happens on.
   */
  tag?: React.ReactNode;
}

/**
 * One stop on the timeline under a summary's total.
 *
 * The line is drawn by the dot's own column rather than by a rule behind the
 * block, so it starts and stops exactly at the dots however tall the text
 * beside it runs.
 *
 * Shared by the two summaries that draw one — the checkout's `OrderSummary` and
 * the trial's `TrialSummary`. They tell different stories over a different
 * number of stops, but a dot, a line and two lines of text is one thing, and
 * two copies of it are how the checkout and the trial would quietly stop
 * looking like the same product.
 */
const Milestone: React.FC<MilestoneProps> = ({
  title,
  detail,
  done = false,
  last = false,
  tag,
}) => (
  <li className="flex gap-3">
    <div className="flex flex-col items-center" aria-hidden="true">
      <span
        className={`mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full ${
          done ? "bg-brand-dark" : "border-[1.5px] border-[#C9D2D8] bg-white"
        }`}
      />
      {!last && <span className="w-px flex-1 bg-[#D9E1E6]" />}
    </div>
    <div className={last ? "" : "pb-4"}>
      {/* The tag wraps under the title on a narrow card rather than squeezing
          it, which is why this is a flex row and not a span in the sentence. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <p className="text-[13px] 3xl:text-[14px] font-medium text-ink">
          {title}
        </p>
        {tag}
      </div>
      <p className="mt-0.5 text-[12px] 3xl:text-[13px] leading-[1.5] text-ink-muted">
        {detail}
      </p>
    </div>
  </li>
);

export default Milestone;
